import { Button } from '@bosch/react-frok'

import type { StartupStep } from './types'

import './StartupSplash.scss'

export interface StartupSplashProps {
    title: string
    subtitle?: string
    /** Picture above the steps (the illustration of the app) */
    illustration?: string
    /** Short silent video above the steps, instead of a picture (it loops) */
    video?: string
    steps: StartupStep[]
    retryLabel: string
}

/** "Pending - Waiting for ..." with the state in bold, like the design. */
const StepDetail = ({ text }: { text: string }) => {
    const [state, ...rest] = text.split(' - ')

    return (
        <span className="startup-step-detail">
            {rest.length > 0 ? (
                <>
                    <b>{state}</b> - {rest.join(' - ')}
                </>
            ) : (
                text
            )}
        </span>
    )
}

/** A filled circle with the two hands of a clock, drawn here: the icon of the design system has no filled one. */
const PendingClock = () => (
    <svg className="startup-step-clock" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="12" />
        <path d="M12 5.5V12h5.4" />
    </svg>
)

/** A filled green circle with a white tick: drawn like the clock and the cross, so that all icons have the same size. */
const DoneTick = () => (
    <svg className="startup-step-tick" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="12" />
        <path d="M7 12.4l3.6 3.6L17.2 8.8" />
    </svg>
)

/** A filled red circle with a white cross, like the clock drawn here. */
const ErrorCross = () => (
    <svg className="startup-step-cross" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="12" />
        <path d="M7.5 7.5l9 9M16.5 7.5l-9 9" />
    </svg>
)

/** The steps that did not start fade out one after the other, the further away the paler. */
const pendingOpacity = (rank: number) => Math.max(0.12, 0.42 - rank * 0.15)

/**
 * What the app is doing while it starts: each step (server, sign-in, spaces, configuration) shows whether it waits,
 * runs, is done or failed, and a failed step can be tried again.
 */
const StartupSplash = ({
    title,
    subtitle,
    illustration,
    video,
    steps,
    retryLabel,
}: StartupSplashProps) => (
    <div className="startup-splash">
        <div className="startup-splash-header">
            <h1 className="startup-splash-title">{title}</h1>
            {subtitle && <p className="startup-splash-subtitle">{subtitle}</p>}
        </div>
        {video ? (
            <video
                className="startup-splash-illustration"
                src={video}
                autoPlay
                loop
                muted
                playsInline
                aria-hidden="true"
            />
        ) : (
            illustration && (
                <img
                    className="startup-splash-illustration"
                    src={illustration}
                    alt=""
                />
            )
        )}
        <ul className="startup-splash-steps" aria-live="polite">
            {steps.map((step, index) => {
                const rank = steps
                    .slice(0, index)
                    .filter((previous) => previous.status === 'pending').length
                // Always set, so that a step that starts fades in instead of jumping
                const style = {
                    opacity:
                        step.status === 'pending' ? pendingOpacity(rank) : 1,
                }

                return (
                    <li
                        key={step.id}
                        className={`startup-step -${step.status}`}
                        data-status={step.status}
                        style={style}
                    >
                        <span
                            className="startup-step-icon"
                            aria-hidden="true"
                            key={step.status}
                        >
                            {step.status === 'loading' ? (
                                <span
                                    className="startup-step-spinner"
                                    data-testid="spinner"
                                />
                            ) : step.status === 'pending' ? (
                                <PendingClock />
                            ) : step.status === 'error' ? (
                                <ErrorCross />
                            ) : (
                                <DoneTick />
                            )}
                        </span>
                        <span className="startup-step-text">
                            <span className="startup-step-title">
                                {step.title}
                            </span>
                            {step.detail && <StepDetail text={step.detail} />}
                        </span>
                        {step.status === 'error' && step.onRetry && (
                            <Button
                                className="startup-step-retry"
                                label={retryLabel}
                                onClick={step.onRetry}
                            />
                        )}
                    </li>
                )
            })}
        </ul>
    </div>
)

export default StartupSplash
